/**
 * The drawer's form per step type — the concept's `Drawer.jsx` forms, typed,
 * against the real settings and the real catalogue.
 *
 * ## THE START STEP EDITS THE SETTINGS, NOT THE NODE
 *
 * `trigger` nodes carry no data in the contract; when people enter lives in
 * `settings.trigger`, with the audience, re-entry and the guardrails beside
 * it. So the Start drawer is a settings form. Its four arms are the contract's
 * four triggers:
 *
 *   - **Segment entry** — any segment; a rule segment ("Dynamic") is the one
 *     that grows on its own, and the picker says which are which. "Also enrol
 *     everyone in it now" is `includeExisting`.
 *   - **Schedule** — once at an IST instant, or recurring daily / weekly (on
 *     chosen weekdays) / monthly (day 1–28, so every month has it) at an IST
 *     time, from a start date to an optional end. It needs an audience: each
 *     occurrence enrols the audience's members as they are then.
 *   - **Event** — a WhatsApp reply (optionally one button's payload), a link
 *     click, an outcome (optionally one type), a Reach lead (optionally one
 *     ad). The audience is an optional filter.
 *   - **Manual** — "Enrol now", once the flow is live.
 *
 * Switching arms keeps the contract's cross-field rules true as it goes
 * (`audienceSegmentId` null for segment entry, a segment for a schedule), so
 * autosave is not left holding settings the API would refuse.
 *
 * ## WHAT THE FORMS NEVER DO
 *
 * Offer a template that is not approved, or an outcome type that does not
 * exist. Show a full address — the test person and run-mode people are the
 * API's masked ones.
 */
import {
  FLOW_HOLDOUT_PORT,
  WEEKDAY_NAMES,
  portsOf,
  type FlowConditionData,
  type FlowEventTrigger,
  type FlowNode,
  type FlowNodeOf,
  type FlowSchedule,
  type FlowSettings,
  type FlowTrigger,
  type SegmentSummary,
  type WaVariableBinding,
} from '@wend/shared';
import { Checkbox, SegmentedToggle, cn } from '@wend/ui';
import type { ReactNode } from 'react';

import { ConditionRules } from './ConditionRules';
import { CTRL, CalloutNote, Field, Fieldset, HINT, IntInput } from './controls';
import { IcLock, IcPlus, IcTrash } from './icons';
import { TemplateText } from './Node';
import {
  WEEKDAY_SHORT,
  defaultBindings,
  emailSpec,
  isoToIstLocal,
  istLocalToIso,
  n,
  todayIst,
  tomorrowAtIst,
  waitText,
  whatsappSpec,
  type FlowEditorData,
} from './model';

const BTN_GHOST =
  'inline-flex cursor-pointer items-center gap-1.5 rounded-[9px] border border-line-strong bg-white/5 px-3 py-2 text-[13px] font-medium text-text hover:bg-white/[0.09] focus-visible:outline-2 focus-visible:outline-brand-b disabled:cursor-not-allowed disabled:opacity-40';
const ICON_BTN =
  'grid size-[30px] flex-none cursor-pointer place-items-center rounded-lg border border-line bg-white/5 p-0 text-muted hover:bg-white/10 hover:text-white focus-visible:outline-2 focus-visible:outline-brand-b';

export { BTN_GHOST, ICON_BTN };

/* -------------------------------------------------------------------------- */
/* Segments                                                                    */
/* -------------------------------------------------------------------------- */

function segmentKind(segment: SegmentSummary): string {
  return segment.type === 'rule' ? 'Dynamic' : segment.type === 'cohort' ? 'Cohort' : 'Static';
}

function SegmentSelect({
  value,
  segments,
  onChange,
  allowNone,
  label,
}: {
  value: string | null;
  segments: readonly SegmentSummary[];
  onChange: (id: string | null) => void;
  allowNone?: string;
  label: string;
}) {
  const known = value === null || segments.some((s) => s.id === value);
  return (
    <select aria-label={label} className={CTRL} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}>
      {allowNone !== undefined && <option value="">{allowNone}</option>}
      {allowNone === undefined && value === null && <option value="">Choose a segment…</option>}
      {!known && <option value={value ?? ''}>A segment that no longer exists</option>}
      {segments.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name} — {n(s.counts.uniqueRecipients)} people · {segmentKind(s)}
        </option>
      ))}
    </select>
  );
}

function SegmentCard({ segment }: { segment: SegmentSummary | undefined }) {
  if (!segment) return null;
  const dynamic = segment.type === 'rule';
  return (
    <div className="flex flex-col gap-1.5 rounded-[10px] border border-line-strong bg-white/[0.03] p-3">
      <div className="flex items-center gap-2">
        <span className={cn('inline-flex rounded-full border px-2 py-0.5 font-mono text-[10.5px]', dynamic ? 'border-brand-b/45 bg-brand-a/20 text-brand-ink' : 'border-info/40 bg-info/12 text-info')}>
          {segmentKind(segment).toLowerCase()}
        </span>
        <strong className="text-[13.5px]" aria-live="polite">{n(segment.counts.uniqueRecipients)} people now</strong>
      </div>
      <p className={HINT}>
        {n(segment.counts.phoneReachable)} reachable on WhatsApp · {n(segment.counts.emailReachable)} by email.{' '}
        {dynamic ? 'Re-evaluated when uploads, leads and events land — people who qualify later enter on their own.' : 'A fixed list: it changes only when its uploads do.'}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Start                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The drawer's toggles may wrap: inline in the phone list the drawer is ~300px
 * wide, and "Next time of day" ran off the edge, clipped (slice 71 review).
 */
const WRAP_TOGGLE = 'flex max-w-full flex-wrap';

const TRIGGER_OPTIONS = [
  { value: 'segment_entry', label: 'Segment' },
  { value: 'schedule', label: 'Schedule' },
  { value: 'event', label: 'Event' },
  { value: 'manual', label: 'Manual' },
] as const;

const EVENT_OPTIONS: readonly { value: FlowEventTrigger['kind']; label: string }[] = [
  { value: 'whatsapp_reply', label: 'Someone replies on WhatsApp' },
  { value: 'link_click', label: 'Someone clicks a link' },
  { value: 'outcome', label: 'An outcome is recorded' },
  { value: 'reach_lead', label: 'A Reach lead arrives' },
];

function defaultEvent(kind: FlowEventTrigger['kind']): FlowEventTrigger {
  switch (kind) {
    case 'whatsapp_reply':
      return { kind, payload: null };
    case 'link_click':
      return { kind };
    case 'outcome':
      return { kind, outcomeTypeId: null };
    case 'reach_lead':
      return { kind, adId: null };
  }
}

function ScheduleForm({ schedule, onChange }: { schedule: FlowSchedule; onChange: (schedule: FlowSchedule) => void }) {
  return (
    <>
      <SegmentedToggle
        label="How often"
        className={WRAP_TOGGLE}
        value={schedule.mode}
        onValueChange={(mode) =>
          onChange(
            mode === 'once'
              ? { mode: 'once', at: tomorrowAtIst(10) }
              : { mode: 'recurring', every: 'day', time: '10:00', weekdays: null, dayOfMonth: null, startsOn: todayIst(), endsOn: null },
          )
        }
        options={[
          { value: 'once', label: 'Once' },
          { value: 'recurring', label: 'Repeating' },
        ]}
      />
      {schedule.mode === 'once' ? (
        <Field label="On (IST)" hint="Everyone in the audience at that moment enters once.">
          <input
            type="datetime-local"
            className={CTRL}
            value={isoToIstLocal(schedule.at)}
            onChange={(e) => {
              const at = istLocalToIso(e.target.value);
              if (at) onChange({ mode: 'once', at });
            }}
          />
        </Field>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Every">
              <select
                className={CTRL}
                value={schedule.every}
                onChange={(e) => {
                  const every = e.target.value as 'day' | 'week' | 'month';
                  onChange({ ...schedule, every, weekdays: every === 'week' ? [1] : null, dayOfMonth: every === 'month' ? 1 : null });
                }}
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </Field>
            <Field label="At (IST)">
              <input type="time" className={CTRL} value={schedule.time} onChange={(e) => /^\d{2}:\d{2}$/.test(e.target.value) && onChange({ ...schedule, time: e.target.value })} />
            </Field>
          </div>
          {schedule.every === 'week' && (
            <div role="group" aria-label="Days of the week" className="flex flex-wrap gap-1.5">
              {WEEKDAY_SHORT.map((day, index) => {
                const on = schedule.weekdays?.includes(index) ?? false;
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={on}
                    onClick={() => {
                      const current = schedule.weekdays ?? [];
                      const next = on ? current.filter((d) => d !== index) : [...current, index].sort((a, b) => a - b);
                      onChange({ ...schedule, weekdays: next });
                    }}
                    className={cn(
                      'min-w-11 cursor-pointer rounded-lg border px-2 py-1.5 text-[12.5px] focus-visible:outline-2 focus-visible:outline-brand-b',
                      on ? 'border-brand-b/60 bg-brand-b/30 text-white' : 'border-line-strong bg-white/5 text-muted',
                    )}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          )}
          {schedule.every === 'month' && (
            <Field label="Day of the month" hint="1 to 28, so every month has it.">
              <IntInput min={1} max={28} value={schedule.dayOfMonth ?? 1} onChange={(dayOfMonth) => onChange({ ...schedule, dayOfMonth })} />
            </Field>
          )}
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Starts on">
              <input type="date" className={CTRL} value={schedule.startsOn} onChange={(e) => e.target.value && onChange({ ...schedule, startsOn: e.target.value })} />
            </Field>
            <Field label="Ends on" hint="Empty: no end.">
              <input type="date" className={CTRL} value={schedule.endsOn ?? ''} onChange={(e) => onChange({ ...schedule, endsOn: e.target.value === '' ? null : e.target.value })} />
            </Field>
          </div>
          <p className={HINT}>Each run enrols the audience as it is at that moment, once per run — never twice for the same run.</p>
        </>
      )}
    </>
  );
}

export function TriggerForm({
  settings,
  data,
  onChange,
  canEnrol,
  onEnrol,
  enrolling,
}: {
  settings: FlowSettings;
  data: FlowEditorData;
  onChange: (settings: FlowSettings) => void;
  canEnrol: boolean;
  onEnrol: (segmentId: string) => void;
  enrolling: boolean;
}) {
  const { trigger } = settings;
  const segments = data.segments;
  const firstSegment = segments[0]?.id ?? null;
  const setTrigger = (next: FlowTrigger, audience: string | null = settings.audienceSegmentId) =>
    onChange({ ...settings, trigger: next, audienceSegmentId: next.type === 'segment_entry' ? null : audience });

  const switchTo = (type: FlowTrigger['type']) => {
    if (type === trigger.type) return;
    if (type === 'segment_entry') {
      if (firstSegment) setTrigger({ type, segmentId: settings.audienceSegmentId ?? firstSegment, includeExisting: false });
    } else if (type === 'schedule') {
      const audience = settings.audienceSegmentId ?? (trigger.type === 'segment_entry' ? trigger.segmentId : firstSegment);
      if (audience) setTrigger({ type, schedule: { mode: 'recurring', every: 'day', time: '10:00', weekdays: null, dayOfMonth: null, startsOn: todayIst(), endsOn: null } }, audience);
    } else if (type === 'event') {
      setTrigger({ type, event: { kind: 'whatsapp_reply', payload: null } });
    } else {
      setTrigger({ type: 'manual' });
    }
  };

  const audienceId = trigger.type === 'segment_entry' ? trigger.segmentId : settings.audienceSegmentId;
  const audience = audienceId ? segments.find((s) => s.id === audienceId) : undefined;

  let arm: ReactNode = null;
  if (trigger.type === 'segment_entry') {
    arm = (
      <>
        <Field label="Starts when someone enters">
          <SegmentSelect label="Segment" value={trigger.segmentId} segments={segments} onChange={(id) => id && setTrigger({ ...trigger, segmentId: id })} />
        </Field>
        <SegmentCard segment={audience} />
        <Checkbox
          size="sm"
          checked={trigger.includeExisting}
          onCheckedChange={(includeExisting) => setTrigger({ ...trigger, includeExisting })}
          label="Also enrol everyone in it now, once, at launch"
          description="Otherwise only people who enter after launch start the flow."
        />
      </>
    );
  } else if (trigger.type === 'schedule') {
    arm = (
      <>
        <Field label="Audience" hint="A scheduled flow enrols this segment's members on each run.">
          <SegmentSelect label="Audience" value={settings.audienceSegmentId} segments={segments} onChange={(id) => id && onChange({ ...settings, audienceSegmentId: id })} />
        </Field>
        <SegmentCard segment={audience} />
        <ScheduleForm schedule={trigger.schedule} onChange={(schedule) => setTrigger({ type: 'schedule', schedule })} />
      </>
    );
  } else if (trigger.type === 'event') {
    const { event } = trigger;
    arm = (
      <>
        <Field label="Starts when">
          <select className={CTRL} value={event.kind} onChange={(e) => setTrigger({ type: 'event', event: defaultEvent(e.target.value as FlowEventTrigger['kind']) })}>
            {EVENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
        {event.kind === 'whatsapp_reply' && (
          <Field label="Only this button’s payload" hint="Empty: any reply starts it.">
            <input className={CTRL} value={event.payload ?? ''} maxLength={256} onChange={(e) => setTrigger({ type: 'event', event: { kind: 'whatsapp_reply', payload: e.target.value.trim() === '' ? null : e.target.value } })} />
          </Field>
        )}
        {event.kind === 'outcome' && (
          <Field label="Outcome type">
            <select className={CTRL} value={event.outcomeTypeId ?? ''} onChange={(e) => setTrigger({ type: 'event', event: { kind: 'outcome', outcomeTypeId: e.target.value === '' ? null : e.target.value } })}>
              <option value="">Any outcome</option>
              {data.outcomeTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </Field>
        )}
        {event.kind === 'reach_lead' && (
          <Field label="Only leads from this ad" hint="The ad id from Reach → Leads. Empty: any ad.">
            <input className={CTRL} value={event.adId ?? ''} maxLength={64} onChange={(e) => setTrigger({ type: 'event', event: { kind: 'reach_lead', adId: e.target.value.trim() === '' ? null : e.target.value.trim() } })} />
          </Field>
        )}
        <Field label="Only people in" hint="Optional. Everyone the event happens to can enter when empty.">
          <SegmentSelect label="Audience filter" value={settings.audienceSegmentId} segments={segments} allowNone="Anyone" onChange={(id) => onChange({ ...settings, audienceSegmentId: id })} />
        </Field>
      </>
    );
  } else {
    arm = (
      <>
        <Field label="Audience" hint="Optional. “Enrol now” adds this segment's members.">
          <SegmentSelect label="Audience" value={settings.audienceSegmentId} segments={segments} allowNone="Nobody yet" onChange={(id) => onChange({ ...settings, audienceSegmentId: id })} />
        </Field>
        <SegmentCard segment={audience} />
        {settings.audienceSegmentId && (
          <button type="button" className={BTN_GHOST} disabled={!canEnrol || enrolling} onClick={() => settings.audienceSegmentId && onEnrol(settings.audienceSegmentId)}>
            {enrolling ? 'Enrolling…' : 'Enrol now'}
          </button>
        )}
        {!canEnrol && <p className={HINT}>Enrolling opens once the flow is live.</p>}
      </>
    );
  }

  return (
    <>
      <SegmentedToggle label="Trigger" className={WRAP_TOGGLE} value={trigger.type} onValueChange={(v) => switchTo(v as FlowTrigger['type'])} options={TRIGGER_OPTIONS} />
      {segments.length === 0 && (trigger.type === 'segment_entry' || trigger.type === 'schedule') && <p className={HINT}>Create a segment first.</p>}
      {arm}
      <Field label="Re-entry">
        <select
          className={CTRL}
          value={settings.reentry}
          onChange={(e) => {
            const reentry = e.target.value as FlowSettings['reentry'];
            onChange({ ...settings, reentry, reentryDays: reentry === 'after_days' ? (settings.reentryDays ?? 30) : null });
          }}
        >
          <option value="never">Once ever</option>
          <option value="after_days">Again after some days</option>
          <option value="always">Every time they qualify</option>
        </select>
      </Field>
      {settings.reentry === 'after_days' && (
        <Field label="Days before they can enter again">
          <IntInput min={1} max={365} value={settings.reentryDays ?? 30} onChange={(reentryDays) => onChange({ ...settings, reentryDays })} />
        </Field>
      )}
      <Fieldset legend="Guardrails">
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Quiet from (IST)">
            <select className={CTRL} value={settings.quietHours.start} onChange={(e) => onChange({ ...settings, quietHours: { ...settings.quietHours, start: Number(e.target.value) } })}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
          </Field>
          <Field label="Until (IST)">
            <select className={CTRL} value={settings.quietHours.end} onChange={(e) => onChange({ ...settings, quietHours: { ...settings.quietHours, end: Number(e.target.value) } })}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="At most (messages)">
            <IntInput min={1} max={20} value={settings.frequencyCap.max} onChange={(max) => onChange({ ...settings, frequencyCap: { ...settings.frequencyCap, max } })} />
          </Field>
          <Field label="Per (days)">
            <IntInput min={1} max={30} value={settings.frequencyCap.perDays} onChange={(perDays) => onChange({ ...settings, frequencyCap: { ...settings.frequencyCap, perDays } })} />
          </Field>
        </div>
        <p className={HINT}>The cap counts Connect campaigns too: someone a campaign already messaged has the flow's message held until the cap clears.</p>
        <Checkbox
          size="sm"
          checked={settings.restCritical}
          onCheckedChange={(restCritical) => onChange({ ...settings, restCritical })}
          label="Rest people whose message pressure is Critical"
          description="Holds them a day, up to three times, then lets them leave the flow rested."
        />
        <Checkbox
          size="sm"
          checked={settings.holdoutPct !== null}
          onCheckedChange={(on) => onChange({ ...settings, holdoutPct: on ? 10 : null })}
          label="Hold some people out at entry"
          description="They get nothing; Sense compares their conversions with everyone else's."
        />
        {settings.holdoutPct !== null && (
          <Field label="Holdout %">
            <IntInput min={1} max={50} value={settings.holdoutPct} onChange={(holdoutPct) => onChange({ ...settings, holdoutPct })} />
          </Field>
        )}
      </Fieldset>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Message                                                                     */
/* -------------------------------------------------------------------------- */

/** Where a variable can come from: the contact's own fields, then every uploaded column. */
function bindingSources(data: FlowEditorData): { value: string; label: string }[] {
  const columns = (data.fields ?? []).filter((f) => f.origin.kind === 'column' && !f.hidden).map((f) => ({ value: f.key, label: f.label }));
  return [
    { value: 'name', label: 'Name' },
    { value: 'email', label: 'Email' },
    { value: 'phoneE164', label: 'Phone' },
    ...columns,
  ];
}

export function MessageForm({
  node,
  data,
  onReplace,
  edgesFrom,
}: {
  node: FlowNodeOf<'message'>;
  data: FlowEditorData;
  onReplace: (node: FlowNode) => void;
  edgesFrom: number;
}) {
  const d = node.data;
  const list = data.templates.filter((t) => t.channel === d.channel);
  const tpl = d.templateId ? data.templates.find((t) => t.id === d.templateId) : undefined;
  const shape = d.channel === 'whatsapp' && d.templateId ? data.waShapes.get(d.templateId) : undefined;
  const sources = bindingSources(data);

  const setChannel = (channel: string) => {
    if (channel === d.channel) return;
    const spec =
      channel === 'email'
        ? emailSpec(data.templates.find((t) => t.channel === 'email')?.id ?? null)
        : (() => {
            const first = data.templates.find((t) => t.channel === 'whatsapp')?.id ?? null;
            return whatsappSpec(first, first ? data.waShapes.get(first) : undefined);
          })();
    onReplace({ ...node, data: spec.data as FlowNodeOf<'message'>['data'] });
  };

  const setBinding = (position: number, patch: Partial<WaVariableBinding>) => {
    if (d.channel !== 'whatsapp') return;
    onReplace({ ...node, data: { ...d, vars: d.vars.map((b) => (b.position === position ? { ...b, ...patch } : b)) } });
  };

  return (
    <>
      <Field label="Channel">
        <SegmentedToggle
          label="Channel"
          value={d.channel}
          onValueChange={setChannel}
          options={[
            { value: 'whatsapp', label: 'WhatsApp' },
            { value: 'email', label: 'Email' },
          ]}
        />
      </Field>
      {d.channel === 'whatsapp' && edgesFrom > 0 && d.buttons.length > 0 && (
        <p className={HINT}>Switching to email removes the quick-reply paths; “No reply” continues as “After the email”.</p>
      )}
      <Field label="Template" hint="Only approved templates are listed.">
        <select
          className={CTRL}
          value={d.templateId ?? ''}
          onChange={(e) => {
            const templateId = e.target.value === '' ? null : e.target.value;
            if (d.channel === 'whatsapp') {
              const next = templateId ? data.waShapes.get(templateId) : undefined;
              onReplace({ ...node, data: { ...d, templateId, buttons: next?.buttons ?? [], vars: defaultBindings(next?.positions ?? [], d.vars) } });
            } else {
              onReplace({ ...node, data: { ...d, templateId } });
            }
          }}
        >
          <option value="">Choose a template…</option>
          {list.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </Field>
      {list.length === 0 && <p className={HINT}>No approved {d.channel === 'email' ? 'email' : 'WhatsApp'} templates yet.</p>}

      {d.channel === 'email' && tpl && (
        <div className="rounded-[10px] border border-info/28 bg-info/7 px-3 py-2.5">
          <div className="mb-1 text-[13px] font-semibold text-white">{tpl.subject ?? '(no subject)'}</div>
          <div className="text-[12.5px] text-muted">{tpl.name}</div>
        </div>
      )}
      {d.channel === 'email' && (
        <Fieldset legend="Sender">
          <Field label="From address" hint="Empty: the workspace's default sender.">
            <input type="email" className={CTRL} value={d.fromEmail ?? ''} onChange={(e) => onReplace({ ...node, data: { ...d, fromEmail: e.target.value.trim() === '' ? null : e.target.value.trim() } })} />
          </Field>
          <Field label="From name">
            <input className={CTRL} value={d.fromName ?? ''} maxLength={120} onChange={(e) => onReplace({ ...node, data: { ...d, fromName: e.target.value.trim() === '' ? null : e.target.value } })} />
          </Field>
        </Fieldset>
      )}

      {d.channel === 'whatsapp' && shape && (
        <div className="rounded-[4px_12px_12px_12px] border border-good/32 bg-good/14 px-3 py-2.5 text-[13px] leading-normal text-[#E6FFF2]">
          {shape.header && <b className="mb-1 block">{shape.header}</b>}
          <TemplateText text={shape.body} names={new Map(d.vars.map((b) => [b.position, b.source.replace(/^extras\./, '')]))} />
          {shape.footer && <span className="mt-1 block text-[11.5px] text-good/70">{shape.footer}</span>}
        </div>
      )}
      {d.channel === 'whatsapp' && d.vars.length > 0 && (
        <Fieldset legend="Variables">
          {d.vars.map((binding) => (
            <div key={binding.position} className="grid grid-cols-2 items-center gap-1.5">
              <code className="col-span-2 justify-self-start text-[11.5px]">{`{{${binding.position}}}`}</code>
              <select aria-label={`Source for {{${binding.position}}}`} className={CTRL} value={binding.source} onChange={(e) => setBinding(binding.position, { source: e.target.value })}>
                {!sources.some((s) => s.value === binding.source) && <option value={binding.source}>{binding.source}</option>}
                {sources.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <input aria-label={`If empty, for {{${binding.position}}}`} placeholder="If empty" className={CTRL} value={binding.fallback} maxLength={200} onChange={(e) => setBinding(binding.position, { fallback: e.target.value })} />
            </div>
          ))}
          <p className={HINT}>“If empty” is used when the profile has no value — WhatsApp refuses an empty parameter, so it is required.</p>
        </Fieldset>
      )}
      {d.channel === 'whatsapp' && (
        <Fieldset legend="Quick replies">
          {d.buttons.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {d.buttons.map((b) => (
                <span key={b} className="rounded-lg border border-line-strong bg-white/5 px-2.5 py-1 text-[12.5px] text-white">{b}</span>
              ))}
            </div>
          ) : (
            <p className={HINT}>This template has no quick-reply buttons, so the step has one path: after the message.</p>
          )}
          <p className={HINT}>Buttons come from the approved template. Each one is its own path on the canvas.</p>
          {d.buttons.length > 0 && (
            <Field label="No reply after (hours)">
              <IntInput min={1} max={168} value={d.noReplyHours} onChange={(noReplyHours) => onReplace({ ...node, data: { ...d, noReplyHours } })} />
            </Field>
          )}
          <p className={HINT}>People without a WhatsApp number skip this send and leave the flow with the reason recorded — never silently on to the next send.</p>
        </Fieldset>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Wait, split, condition, update, goal, exit, comment                         */
/* -------------------------------------------------------------------------- */

export function WaitForm({ node, onReplace }: { node: FlowNodeOf<'wait'>; onReplace: (node: FlowNode) => void }) {
  const d = node.data;
  const set = (data: FlowNodeOf<'wait'>['data']) => onReplace({ ...node, data });
  return (
    <>
      <SegmentedToggle
        label="Wait type"
        className={WRAP_TOGGLE}
        value={d.mode}
        onValueChange={(mode) =>
          set(mode === 'until' ? { mode: 'until', at: tomorrowAtIst(10) } : mode === 'next' ? { mode: 'next', weekday: null, time: '10:00' } : { mode: 'duration', amount: 1, unit: 'days' })
        }
        options={[
          { value: 'duration', label: 'For a time' },
          { value: 'until', label: 'Until a date' },
          { value: 'next', label: 'Next time of day' },
        ]}
      />
      {d.mode === 'duration' && (
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Amount">
            <IntInput min={1} max={10_000} value={d.amount} onChange={(amount) => set({ ...d, amount })} />
          </Field>
          <Field label="Unit">
            <select className={CTRL} value={d.unit} onChange={(e) => set({ ...d, unit: e.target.value as 'minutes' | 'hours' | 'days' })}>
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </Field>
        </div>
      )}
      {d.mode === 'until' && (
        <Field label="Until (IST)">
          <input
            type="datetime-local"
            className={CTRL}
            value={isoToIstLocal(d.at)}
            onChange={(e) => {
              const at = istLocalToIso(e.target.value);
              if (at) set({ mode: 'until', at });
            }}
          />
        </Field>
      )}
      {d.mode === 'next' && (
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="On">
            <select className={CTRL} value={d.weekday ?? ''} onChange={(e) => set({ ...d, weekday: e.target.value === '' ? null : Number(e.target.value) })}>
              <option value="">Any day</option>
              {WEEKDAY_NAMES.map((name, i) => (
                <option key={name} value={i}>{name}</option>
              ))}
            </select>
          </Field>
          <Field label="At (IST)">
            <input type="time" className={CTRL} value={d.time} onChange={(e) => /^\d{2}:\d{2}$/.test(e.target.value) && set({ ...d, time: e.target.value })} />
          </Field>
        </div>
      )}
      <CalloutNote>Quiet hours still apply: if the wait ends inside them, the next message holds until they end.</CalloutNote>
      <p className={HINT}>Shown on the canvas as “{waitText(d)}”.</p>
    </>
  );
}

export function SplitForm({ node, onReplace, newRowId }: { node: FlowNodeOf<'split'>; onReplace: (node: FlowNode) => void; newRowId: () => string }) {
  const d = node.data;
  const sum = d.rows.reduce((a, r) => a + r.pct, 0) + (d.holdout?.pct ?? 0);
  const set = (data: FlowNodeOf<'split'>['data']) => onReplace({ ...node, data });
  const setRow = (id: string, patch: Partial<FlowNodeOf<'split'>['data']['rows'][number]>) => set({ ...d, rows: d.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
  const even = () => {
    const left = 100 - (d.holdout?.pct ?? 0);
    const each = Math.floor(left / d.rows.length);
    set({ ...d, rows: d.rows.map((r, i) => ({ ...r, pct: i === 0 ? left - each * (d.rows.length - 1) : each })) });
  };
  const add = () => {
    const used = new Set(d.rows.map((r) => r.label));
    const label = ['A', 'B', 'C', 'D', 'E'].find((l) => !used.has(l)) ?? `Path ${d.rows.length + 1}`;
    set({ ...d, rows: [...d.rows, { id: newRowId(), label, pct: 0 }] });
  };
  return (
    <>
      <Fieldset legend="Paths">
        {d.rows.map((r) => (
          <div key={r.id} className="grid grid-cols-[1fr_88px_32px] items-center gap-2">
            <input aria-label={`Name of path ${r.label}`} className={CTRL} value={r.label} maxLength={40} onChange={(e) => setRow(r.id, { label: e.target.value })} />
            <span className="flex items-center gap-1.5 text-muted">
              <IntInput aria-label={`Path ${r.label} percentage`} min={0} max={100} value={r.pct} onChange={(pct) => setRow(r.id, { pct })} className="text-right" />%
            </span>
            {d.rows.length > 1 ? (
              <button type="button" className={ICON_BTN} aria-label={`Remove path ${r.label}`} onClick={() => set({ ...d, rows: d.rows.filter((x) => x.id !== r.id) })}>
                <IcTrash size={14} />
              </button>
            ) : (
              <span aria-hidden="true" className="size-[30px]" />
            )}
          </div>
        ))}
        {d.holdout && (
          <div className="grid grid-cols-[1fr_88px_32px] items-center gap-2">
            <span className="flex items-center gap-1.5 text-[13.5px] text-muted"><IcLock size={12} /> Holdout</span>
            <span className="flex items-center gap-1.5 text-muted">
              <IntInput aria-label="Holdout percentage" min={1} max={50} value={d.holdout.pct} onChange={(pct) => set({ ...d, holdout: { pct } })} className="text-right" />%
            </span>
            <button type="button" className={ICON_BTN} aria-label="Remove the holdout" onClick={() => set({ ...d, holdout: null })}>
              <IcTrash size={14} />
            </button>
          </div>
        )}
        <div role="status" aria-live="polite" className={cn('rounded-lg px-2.5 py-2 text-[12.5px]', sum === 100 ? 'bg-good/8 text-good' : 'bg-bad/10 text-bad')}>
          {sum === 100 ? '✓ Adds up to 100%' : `Adds up to ${sum}% — must be exactly 100% to launch`}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={BTN_GHOST} onClick={even}>Even out</button>
          {d.rows.length < 5 && (
            <button type="button" className={BTN_GHOST} onClick={add}>
              <IcPlus size={14} /> Add path
            </button>
          )}
          {!d.holdout && (
            <button type="button" className={BTN_GHOST} onClick={() => set({ ...d, holdout: { pct: 10 } })}>
              <IcLock size={13} /> Add a holdout
            </button>
          )}
        </div>
      </Fieldset>
      <CalloutNote>The holdout gets no messages. Sense compares their conversions with everyone else's — that difference is the lift this flow causes. People are assigned by a hash of the version and the person, so a re-run never reshuffles them.</CalloutNote>
    </>
  );
}

export function ConditionForm({ node, data, onReplace, newId }: { node: FlowNodeOf<'condition'>; data: FlowEditorData; onReplace: (node: FlowNode) => void; newId: () => string }) {
  return (
    <ConditionRules
      data={node.data}
      fields={data.fields}
      newId={newId}
      onChange={(next: FlowConditionData) => onReplace({ ...node, data: next })}
    />
  );
}

export function UpdateForm({ node, data, onReplace }: { node: FlowNodeOf<'update'>; data: FlowEditorData; onReplace: (node: FlowNode) => void }) {
  const d = node.data;
  const columns = (data.fields ?? []).filter((f) => f.origin.kind === 'column').map((f) => (f.origin.kind === 'column' ? f.origin.column : ''));
  return (
    <>
      <Field label="Action">
        <select
          className={CTRL}
          value={d.action}
          onChange={(e) => {
            const action = e.target.value;
            const value = 'value' in d ? d.value : '';
            onReplace({
              ...node,
              data: action === 'set_field' ? { action: 'set_field', key: 'key' in d ? d.key : 'flow_status', value } : { action: action === 'remove_tag' ? 'remove_tag' : 'add_tag', value: value || 'flow-engaged' },
            });
          }}
        >
          <option value="add_tag">Add a tag</option>
          <option value="remove_tag">Remove a tag</option>
          <option value="set_field">Set a column</option>
        </select>
      </Field>
      {d.action === 'set_field' && (
        <Field label="Column" hint="A column name as it appears in your uploads; a new name adds a column.">
          <input className={CTRL} list={`cols-${node.id}`} value={d.key} maxLength={200} onChange={(e) => onReplace({ ...node, data: { ...d, key: e.target.value } })} />
          <datalist id={`cols-${node.id}`}>
            {columns.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
      )}
      <Field label={d.action === 'set_field' ? 'Value' : 'Tag'}>
        <input className={CTRL} value={d.value} maxLength={d.action === 'set_field' ? 500 : 60} onChange={(e) => onReplace({ ...node, data: { ...d, value: e.target.value } as typeof d })} />
      </Field>
      <p className={HINT}>Written to the person's latest contact row in Core, so segments, Connect and Sense all see it. Tags live in the “tags” column.</p>
    </>
  );
}

export function GoalForm({ node, data, onReplace }: { node: FlowNodeOf<'goal'>; data: FlowEditorData; onReplace: (node: FlowNode) => void }) {
  const d = node.data;
  return (
    <>
      <Field label="Goal" hint={data.outcomeTypes.length === 0 ? 'No outcome types are declared yet — Sense → Outcomes is where you add one.' : undefined}>
        <select className={CTRL} value={d.outcomeTypeId} onChange={(e) => onReplace({ ...node, data: { ...d, outcomeTypeId: e.target.value } })}>
          <option value="any">Any conversion</option>
          {data.outcomeTypes.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </Field>
      <Field label="Counts if it happens within (days of entering)">
        <IntInput min={1} max={365} value={d.withinDays} onChange={(withinDays) => onReplace({ ...node, data: { ...d, withinDays } })} />
      </Field>
      <p className={HINT}>A Matched outcome ends the enrolment as converted wherever the person is in the flow. Conversions are compared with the holdout over the same window.</p>
    </>
  );
}

export function ExitForm({ node, onReplace }: { node: FlowNodeOf<'exit'>; onReplace: (node: FlowNode) => void }) {
  const d = node.data;
  return (
    <>
      <Field label="Label">
        <input className={CTRL} value={d.note ?? ''} maxLength={200} placeholder="e.g. Respect the “Not now”" onChange={(e) => onReplace({ ...node, data: { ...d, note: e.target.value.trim() === '' ? null : e.target.value } })} />
      </Field>
      <Field label="Can re-enter after (days)" hint="0: straight away, if the re-entry rule allows.">
        <IntInput min={0} max={365} value={d.cooloffDays} onChange={(cooloffDays) => onReplace({ ...node, data: { ...d, cooloffDays } })} />
      </Field>
    </>
  );
}

export function CommentForm({ node, onReplace }: { node: FlowNodeOf<'comment'>; onReplace: (node: FlowNode) => void }) {
  return (
    <Field label="Comment" hint="A note for whoever reviews this flow. It is never sent.">
      <textarea rows={4} className={`${CTRL} resize-y`} value={node.data.text} maxLength={1000} onChange={(e) => onReplace({ ...node, data: { text: e.target.value } })} />
    </Field>
  );
}

/** Ports of a node, labelled, for the drawer's connection list. */
export function portLabel(node: FlowNode, portId: string, data: FlowEditorData): string {
  const shape = node.type === 'message' && node.data.channel === 'whatsapp' && node.data.templateId ? data.waShapes.get(node.data.templateId) : undefined;
  if (portId === FLOW_HOLDOUT_PORT) return 'Holdout';
  return portsOf(node, shape ?? null).find((p) => p.id === portId)?.label ?? portId;
}
